import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Settings, FileText, Boxes, Lock } from "lucide-react";
import { WORLDS } from "../../lib/worlds";
import { useApp, ROLE_LABEL } from "../../context/AppCtx";

export const Logo = ({ small }) => (
  <div className="flex items-center gap-3" data-testid="afrikho-logo">
    <svg width={small ? 26 : 34} height={small ? 24 : 30} viewBox="0 0 34 30"><path d="M17 1 L33 29 H1 Z" fill="none" stroke="#C5A059" strokeWidth="2" /><path d="M17 9 L26 25 H8 Z" fill="#C5A059" /><path d="M17 15 L21 22 H13 Z" fill="#FAF9F5" /></svg>
    <div className="leading-none"><div className="font-serif font-bold tracking-[0.12em] text-[17px] text-[#1C1917]">AFRIKHO</div><div className="text-[9px] tracking-[0.32em] text-[#8A847C] mt-1">INTELLIGENCE</div></div>
  </div>
);

function WorldItem({ w, open, onToggle, current }) {
  const { can } = useApp();
  const nav = useNavigate();
  const W = WORLDS[w];
  const allowed = can(w);
  const Icon = W.icon;
  const active = current === w;
  return (
    <div>
      <button data-testid={`sidebar-world-${w}`} onClick={() => { onToggle(w); nav(`/${w === "group" ? "" : w}`); }}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold transition-colors ${active ? "bg-[var(--green)] text-white" : "text-[#44403C] hover:bg-[#F4F1EA]"}`}>
        <Icon size={16} className={active ? "text-[#E9D9B0]" : "text-[#8A847C]"} />
        <span className="flex-1 text-left truncate">{W.label}</span>
        {!allowed && <Lock size={12} className="opacity-60" />}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-5 pl-3 border-l border-[#E5E0D5] my-1 space-y-0.5 rise">
          {W.modules.filter((mm) => can(w, mm.id)).map((mm) => (
            <NavLink key={mm.id} end to={`/${w}/${mm.id}`} data-testid={`nav-${w}-${mm.id}`}
              className={({ isActive }) => `flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[12.5px] transition-colors ${isActive || (active && mm.id === "overview" && isOverview()) ? "bg-[var(--chip)] text-[var(--accent)] font-semibold" : "text-[#57534E] hover:text-[#1C1917] hover:bg-[#FAF9F5]"}`}>
              <mm.icon size={13} />{mm.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
const isOverview = () => { const p = window.location.pathname.split("/").filter(Boolean); return p.length <= 1; };

export function Sidebar({ mobile, onClose }) {
  const loc = useLocation();
  const { role, env } = useApp();
  const current = loc.pathname.split("/")[1] || "group";
  const [open, setOpen] = useState(current);
  React.useEffect(() => { setOpen(current); }, [current]);
  const toggle = (w) => setOpen((o) => (o === w && current === w ? null : w));
  const item = (w) => <WorldItem key={w} w={w} open={open === w} onToggle={toggle} current={current} />;
  return (
    <aside data-testid="sidebar" className={`${mobile ? "w-[290px]" : "w-[272px] hidden lg:flex"} shrink-0 h-screen sticky top-0 bg-white border-r border-[#E5E0D5] flex flex-col`} onClick={(e) => mobile && e.target.closest("a") && onClose?.()}>
      <div className="px-5 pt-6 pb-5"><Logo /></div>
      <nav className="flex-1 overflow-y-auto scroll-thin px-3 space-y-1 pb-4">
        {env !== "afrikho" && <>{item(env)}<div className="mx-3 mt-3 p-3 rounded-xl bg-[#FAF1DC] text-[11px] text-[#8C6D1F]">Conceptual environment · fictional data · no institutional affiliation</div></>}
        {env === "afrikho" && <>
        {["group", "petroleum", "renewable", "capital"].map(item)}
        <div className="pt-3 pb-1 px-3 flex items-center gap-2 text-[10px] tracking-[0.2em] font-bold text-[#A8A29E]"><Boxes size={12} /> ADHOC SOLUTIONS</div>
        <NavLink to="/adhoc" data-testid="sidebar-adhoc-hub" className={({ isActive }) => `block ml-3 px-3 py-1.5 text-[12px] rounded-lg ${isActive ? "text-[var(--accent)] font-semibold bg-[#F4F1EA]" : "text-[#78716C] hover:bg-[#FAF9F5]"}`}>Solutions Hub</NavLink>
        <div className="ml-2">{["mining", "medical"].map(item)}</div>
        <div className="pt-2" />
        {item("ai")}
        </>}
        <div className="border-t border-[#EFECE4] my-3" />
        <NavLink to="/reports" data-testid="sidebar-reports" className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold ${isActive ? "bg-[var(--green)] text-white" : "text-[#44403C] hover:bg-[#F4F1EA]"}`}><FileText size={16} />Reports & Documents</NavLink>
        <NavLink to="/settings" data-testid="sidebar-settings" className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-semibold ${isActive ? "bg-[var(--green)] text-white" : "text-[#44403C] hover:bg-[#F4F1EA]"}`}><Settings size={16} />Settings / Demo Controls</NavLink>
      </nav>
      <div className="p-4 border-t border-[#EFECE4] flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[var(--green)] text-[#E9D9B0] grid place-items-center font-serif font-bold">TM</div>
        <div className="leading-tight"><div className="text-[13px] font-semibold">T. Matyana (demo)</div><div className="text-[11px] text-[#8A847C]" data-testid="sidebar-role-label">{ROLE_LABEL[role]}</div></div>
      </div>
    </aside>
  );
}
